'use client';

import { useState, type JSX } from 'react';

import styles from './AiPoweredAnalyticsPage.module.css';

export interface FaqAccordionItem {
  title: string;
  body: string;
  iconClass: string;
  gradientClass: string;
}

export interface FaqAccordionClientProps {
  items: FaqAccordionItem[];
  /**
   * Optional default-open index. -1 (default) means all closed on initial
   * render; some accordion designs default the first item open.
   */
  defaultOpen?: number;
}

/**
 * Client-side accordion: matches source Bootstrap collapse markup but uses
 * React state to toggle open/closed. Preserves <button> elements so the
 * parity-gate `extractButtons` still counts the 3 FAQ headings as buttons.
 */
export function FaqAccordionClient({
  items,
  defaultOpen = -1,
}: FaqAccordionClientProps): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number>(defaultOpen);

  return (
    <div className={styles.accordion} id="accordionExample">
      {items.map((item, index) => {
        const targetId = `faq-${index + 1}`;
        const headingId = `faq-heading-${index + 1}`;
        const isOpen = openIndex === index;
        return (
          <div
            key={targetId}
            className={styles.faqItem}
            data-cms-field={`faq-${index}`}
          >
            <div className={styles.faqHeader} id={headingId}>
              <button
                type="button"
                className={`${styles.faqBtn} ${isOpen ? styles.faqBtnOpen : ''}`.trim()}
                aria-expanded={isOpen}
                aria-controls={targetId}
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
              >
                <i
                  className={`${item.iconClass} ${item.gradientClass} ${styles.faqIcon}`}
                  aria-hidden="true"
                />{' '}
                <span>{item.title}</span>
              </button>
            </div>
            <div
              id={targetId}
              className={`${styles.faqBody} ${isOpen ? styles.faqBodyOpen : styles.faqBodyClosed}`.trim()}
              aria-labelledby={headingId}
              role="region"
              hidden={!isOpen}
            >
              <div className={styles.faqBodyInner}>
                <p>{item.body}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
